<template>
  <div class="menu">
    <div class="left">
      <ul
        class="links-list"
        :class="
          !isMobileOrTablet &&
          headerData.hasSearchBar &&
          (searchBoxIsBox || searchBoxFullWidth)
            ? '!w-0'
            : 'w-full'
        "
      >
        <li
          v-if="headerData.showCategories"
          v-click-outside="closeExperiencesMenu"
          class="menu-item"
        >
          <button
            class="menu-button"
            @click="experiencesMenuStatus = !experiencesMenuStatus"
          >
            {{ t(headerData.categories.title) }}
            <icon-chevron-black class="chevron mx-3" />
          </button>
          <TheHeaderExperiencesList
            :is-open="experiencesMenuStatus"
            @close="closeExperiencesMenu"
          />
        </li>
        <li
          v-for="(link, index) in headerlinksCheck"
          :key="index"
          class="menu-item"
        >
          <NuxtLink
            :to="link.itsOuterLink ? link.target : localePath(link.target)"
            :target="link.itsOuterLink ? '_blank' : '_self'"
            class="menu-button"
          >
            {{ t(link?.title) }}
          </NuxtLink>
        </li>
        <template v-if="headerData.links.length > 5">
          <li v-if="!isMobileOrTablet" class="menu-item max-lg:!py-0">
            <TheHeaderLinksDropdown
              :links="headerData.links.slice(4)"
              class="menu-button"
            />
          </li>
          <template v-else>
            <li
              v-for="link in headerData.links.slice(4)"
              :key="link.title"
              class="menu-item"
            >
              <NuxtLink
                :to="link.itsOuterLink ? link.target : localePath(link.target)"
                :target="link.itsOuterLink ? '_blank' : '_self'"
                class="menu-button"
              >
                {{ t(link?.title) }}
              </NuxtLink>
            </li>
          </template>
        </template>
      </ul>
      <SearchBoxTwo
        v-show="
          headerData.hasSearchBar
            ? isTemplate4
              ? searchBoxIsBox || searchBoxFullWidth
              : true
            : false
        "
        :is-box="searchBoxStatus"
        :top-cities="topCities"
        :class="searchBoxFullWidth ? 'active' : 'w-6'"
        class="main-search max-lg:hidden"
        @update-full-width-status="searchBoxFullWidth = $event"
      />
    </div>
    <div class="right-menu">
      <NuxtLink
        v-if="config.public.header?.actionButton"
        class="menu-button border-[1px] w-max px-3 py-2 rounded-md"
        :to="config.public.header?.actionButton?.target"
        target="_blank"
      >
        {{ config.public.header.actionButton.label }}
      </NuxtLink>

      <TheHeaderSwitcher
        v-if="headerData.isMultiLanguage && checkMultiLang"
        :active-item="getActiveLang"
        :show-code="false"
        :list="availableLocales"
        :header-data="headerData"
        is-centered
        :route-path="routePath"
        @update:active-item="switchLanguage"
      />

      <TheHeaderSwitcher
        v-if="headerData.isMultiCurrency && checkMultiCurrency"
        :list="simplifycurrencies(currenciesList)"
        :active-item="getActiveCurrency"
        :header-data="headerData"
        :route-path="routePath"
        @update:active-item="switchCurrency"
      />

      <Cart v-if="!isMobileOrTablet" @open-login="emit('open-login', $event)" />
      <a
        v-if="isSupplier && headerData.phone"
        :tel="headerData.phone"
        :href="`tel:${headerData.phone}`"
        class="phone-call"
      >
        <icon-phone-in-call />
        <span dir="ltr"> {{ headerData.phone }} </span>
      </a>

      <div class="md:hidden absolute bottom-10">
        <LayoutTheFooterSocialMedias
          :socials="config.public.footer.socialLinks"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, type PropType, ref } from "vue";
import { useRouter } from "vue-router";
import { useCookie } from "nuxt/app";
import type { ICurrency, ITopCities } from "../../types/general";
import TheHeaderSwitcher from "./TheHeaderSwitcher.vue";
import TheHeaderLinksDropdown from "./TheHeaderLinksDropdown.vue";
import TheHeaderExperiencesList from "./TheHeaderExperiencesList.vue";
import SearchBoxTwo from "../search-box-two/SearchBoxTwo.vue";
import { useOneTap, type CredentialResponse } from "vue3-google-signin";

const { t, locales } = useI18n();
const router = useRouter();
const config: any = useRuntimeConfig();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();
const { isLoggedIn } = useAuth();

const emit = defineEmits(["open-login"]);
const props = defineProps({
  headerData: {
    type: Object,
    default: () => {
      return {};
    },
  },
  searchBoxIsBox: { type: Boolean, default: false },
  isSupplier: { type: Boolean, default: false },
  routePath: { type: String, default: "/" },
  currenciesList: { type: Array as PropType<ICurrency[]>, required: true },
  topCities: { type: Array as PropType<ITopCities[]>, required: true },
});

const isTemplate4 = props.headerData?.type === "template4";
const isSupplier4 = props.headerData?.type === "supplier4";

const activeLang = useCookie<string>("locale");
const activeCurrency = useCookie<string>("currency");
const windowWidth = ref<number>(0);
const experiencesMenuStatus = ref<boolean>(false);

const closeExperiencesMenu = () => {
  if (experiencesMenuStatus.value) {
    experiencesMenuStatus.value = false;
  }
};

const headerlinksCheck = computed(() => {
  return props.headerData.links?.length > 5
    ? props.headerData.links?.slice(0, 4)
    : props.headerData?.links;
});

const checkMultiLang = computed(() => {
  return locales.value && locales.value.length > 1;
});

const checkMultiCurrency = computed(() => {
  return props.currenciesList && props.currenciesList.length > 1;
});

const getActiveLang = computed(() => {
  return activeLang.value || "";
});

const getActiveCurrency = computed(() => {
  return activeCurrency.value || "";
});

const availableLocales = computed(() => {
  return locales.value;
});

const isMobileOrTablet = computed(() => {
  return windowWidth.value > 0 && windowWidth.value < 1024;
});

const searchBoxStatus = computed(() => {
  return isMobileOrTablet.value ? true : props.searchBoxIsBox;
});
const searchBoxFullWidth = ref<boolean>(searchBoxStatus.value);

const switchLanguage = (langCode: string) => {
  if (langCode !== activeLang.value) {
    activeLang.value = langCode;
    router.push(switchLocalePath(langCode));
  }
};

const switchCurrency = (newCurrencyCode: string) => {
  if (newCurrencyCode !== activeCurrency.value) {
    activeCurrency.value = newCurrencyCode;
    window.location.reload();
  }
};

const simplifycurrencies = (original: ICurrency[] | null) => {
  const simplified = original?.map((item) => ({
    name: item?.title,
    code: item.currency_code,
    id: item.pivot.currency_id,
  }));

  return simplified || [];
};

const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  windowWidth.value = window.innerWidth;
  window.addEventListener("resize", handleResize);

  if (!isLoggedIn) {
    useOneTap({
      onSuccess: (response: CredentialResponse) => {
        console.log("Success:", response);
      },
      onError: () => console.error("Error with One Tap Login"),
      // options
    });
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
